import {
  Button,
  Col,
  DatePicker,
  Form,
  InputNumber,
  Row,
  Select,
  Space,
  Spin,
  Table,
  message,
  Typography,
  Checkbox,
} from 'antd';
import { Icon } from 'components';
import moment from 'moment';
import React, { useEffect } from 'react';
import { AiOutlineCaretDown } from 'react-icons/ai';
import { getDocumentDetail, requestApproval, updateApproval } from 'services/session';
import { forEach, uniq } from 'lodash';
import {
  // MailCC,
  OwnDesktops,
  RequestInfo,
  UserSearchInput,
} from './components';

function DesktopCreate({ readOnly, form, id, onClose }) {
  const [visibleOwnDesktops, setVisibleOwnDesktops] = React.useState(false);
  const [user, setUser] = React.useState('');
  const [userName, setUserName] = React.useState('');
  const [configSiteJSON, setConfigSiteJSON] = React.useState({});
  const [configLoaded, setConfigLoaded] = React.useState(false);
  const type = 'approval.types.create-vd';

  function getErrorFields(items) {
    const errObjs = {};

    forEach(items, (errItem) => {
      const namePath = errItem.name;
      const nameString = namePath.join('/');
      if (!errObjs[nameString]) {
        errObjs[nameString] = [];
      }

      errObjs[nameString].push(errItem.reason);
    });

    return Object.entries(errObjs).map(([k, v]) => ({ name: k.split('/'), errors: uniq(v) }));
  }

  // config.site.json 로드 (readOnly가 false일 때만)
  useEffect(() => {
    if (readOnly) {
      setConfigLoaded(true);
      return;
    }
    fetch(`${process.env.PUBLIC_URL}/config.site.json`)
      .then((res) => (res.ok ? res.text() : ''))
      .then((text) => {
        if (text) {
          try {
            setConfigSiteJSON(JSON.parse(text));
          } catch {
            // JSON 파싱 실패 시 무시
          }
        }
        setConfigLoaded(true);
      });
  }, []);

  useEffect(() => {
    if (!id) {
      const applicant = localStorage.getItem('userId');
      form.setFieldsValue({
        applicant,
        type: 'approval.types.create-vd',
        contents: [
          {
            id: Date.now() + 0,
            vCpu: 4,
            memory: 8,
            disk: 80,
            locale: 'ko',
          },
        ],
      });

      return;
    }

    getDocumentDetail(id).then((data) => {
      let approveDate = data?.approveDate;

      if (approveDate !== null) {
        approveDate = moment(approveDate);
      }
      const contents1 = data?.contents?.map((c) => ({
        vCpu: c.vCpu,
        memory: c.memory,
        disk: c.disk,
        locale: c.locale,
      }));
      form.setFieldsValue({
        ...data,
        contents: contents1,
        approveDate,
        applyDate: moment(data.applyDate).format('YYYY-MM-DD HH:mm:ss'),
      });
      setUserName(data?.userName);
      setUser(data?.user);
    });
  }, [id]);

  // config 로드 전까지 로딩 스피너 표시
  if (!configLoaded) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 50 }}>
        <Spin />
      </div>
    );
  }

  return (
    <Form
      form={form}
      layout="horizontal"
      labelCol={{ span: 3 }}
      wrapperCol={{ span: 21 }}
      onFinish={async ({ contents, approveDate, ...fields }) => {
        const contents1 = { ...contents[0], ownDesktop: '' };
        const payload = {
          ...fields,
          contents: JSON.stringify([contents1]),
          approveDate: approveDate?.format('YYYY-MM-DD HH:mm:ss'),
        };
        delete payload.checkedAlert;

        try {
          const result = id ? await updateApproval(id, payload) : await requestApproval(payload);
          if (result.data.status === 'error') {
            const errors = getErrorFields(result.data.invalidParams);
            form.setFields(errors);
            return;
          }
          form.resetFields();
          onClose();
        } catch (e) {
          message.error(e);
        }
      }}
    >
      <RequestInfo readOnly={readOnly} />
      <Form.Item label="신청 종류" name="type" required>
        VD 생성
      </Form.Item>
      <Form.Item label="사용자(ID)" required>
        <Row gutter={[0, 4]}>
          <Col span={24}>
            <Space style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Form.Item
                name="user"
                rules={[{ required: true, message: '필수 입력 항목 입니다.' }]}
              >
                <UserSearchInput
                  readOnly1={readOnly}
                  onSuccess={async (uid) => {
                    // 처리
                    setUser(uid);
                  }}
                  userName={userName}
                />
              </Form.Item>
              {/* TODO: 선택한 사용자가 있을 때 아래 버튼 보이기 */}
              <Button
                type="text"
                className="ant-dropdown-link"
                onClick={() => {
                  setUser(form.getFieldValue('user'));
                  setVisibleOwnDesktops(!visibleOwnDesktops);
                }}
              >
                보유 VD 확인
                <Icon name={<AiOutlineCaretDown className="anticon anticon-down" />} />
              </Button>
            </Space>
          </Col>
          {visibleOwnDesktops && (
            // TODO: show/hide 에니메이션 추가 https://motion.ant.design/components/animate#components-animate-demo-simple
            <Col span={24}>
              <OwnDesktops
                title={false}
                readOnly
                bodyStyle={{ padding: 0 }}
                user={user}
                apprType={type}
              />
            </Col>
          )}
        </Row>
      </Form.Item>
      <Form.Item label="신청 Desktop" name="contents" valuePropName="dataSource" required>
        <Table
          size="small"
          rowKey="id"
          pagination={false}
          columns={[
            {
              title: 'CPU',
              dataIndex: 'vCpu',
              width: 100,
              render: (text, record, index) => {
                return readOnly ? (
                  `${text} Core`
                ) : (
                  <Form.Item name={['contents', `${index}`, 'vCpu']}>
                    <InputNumber
                      addonAfter="Core"
                      initialValues={text}
                      min={0}
                      onChange={() => {
                        form.setFields([{ name: ['contents', `${index}`, 'vCpu'], errors: [] }]);
                      }}
                    />
                  </Form.Item>
                );
              },
            },
            {
              title: 'Memory',
              dataIndex: 'memory',
              width: 100,
              render: (text, record, index) =>
                readOnly ? (
                  `${text} GB`
                ) : (
                  <Form.Item name={['contents', `${index}`, 'memory']}>
                    <InputNumber
                      addonAfter="GB"
                      initialValues={text}
                      min={0}
                      onChange={() => {
                        form.setFields([{ name: ['contents', `${index}`, 'memory'], errors: [] }]);
                      }}
                    />
                  </Form.Item>
                ),
            },
            {
              title: 'Disk',
              dataIndex: 'disk',
              width: 100,
              render: (text, record, index) =>
                readOnly ? (
                  `${text} GB`
                ) : (
                  <Form.Item name={['contents', `${index}`, 'disk']}>
                    <InputNumber
                      addonAfter="GB"
                      min={0}
                      initialValues={text}
                      onChange={() => {
                        form.setFields([{ name: ['contents', `${index}`, 'disk'], errors: [] }]);
                      }}
                    />
                  </Form.Item>
                ),
            },
            {
              title: '언어',
              dataIndex: 'locale',
              render: (text) =>
                readOnly ? (
                  text
                ) : (
                  <Select defaultValue="ko">
                    <Select.Option value="ko">ko</Select.Option>
                  </Select>
                ),
            },
          ]}
        />
      </Form.Item>
      <Form.Item
        label="작업 희망 일시"
        name="approveDate"
        extra="작업 희망 일시를 설정하지 않거나 이미 경과된 경우 승인 시점에 처리됩니다."
        rules={[
          () => ({
            validator(_, value) {
              if (value > moment() || !value) {
                return Promise.resolve();
              }
              return Promise.reject(new Error('현재보다 이전 시간은 설정할 수 없습니다.'));
            },
          }),
        ]}
      >
        <DatePicker
          showTime={{
            defaultValue: moment('00:00', 'HH:mm'),
            format: 'HH:mm',
          }}
          inputReadOnly={1}
          disabled={readOnly}
          placeholder=""
          disabledDate={(current) => current && current < moment().add(-1, 'day')}
          showNow={false}
        />
      </Form.Item>

      {configSiteJSON.alertMessage ? (
        <Form.Item label="필수사항" required>
          <Space size="large">
            <Typography.Text style={{ whiteSpace: 'pre-line' }}>
              {configSiteJSON.alertMessage}
            </Typography.Text>
            <Form.Item
              name="checkedAlert"
              valuePropName="checked"
              noStyle
              rules={[
                {
                  validator(_, value) {
                    if (value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error('필수사항을 확인해주세요.'));
                  },
                },
              ]}
            >
              <Checkbox>확인</Checkbox>
            </Form.Item>
          </Space>
        </Form.Item>
      ) : undefined}
    </Form>
  );
}

export default DesktopCreate;
